import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Minimize2 } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys } from '~/api/queryKeys';
import { Button } from '~/components/ui/button';

import {
  useActiveCatalogImport,
  type ActiveCatalogImportWithSource,
} from '../hooks/use-active-catalog-import';
import { useWorkflowImportStatus } from '../hooks/use-catalog-import';
import {
  isNotIngestionRun,
  useIngestCurrentPage,
  useIngestRunLineage,
} from '../hooks/use-ingest-run';
import { computeCatalogProgress, findRun, visibleEtaSeconds } from '../utils/ingest-run';

/**
 * Global, always-mounted watcher for an in-flight catalog import (mounted in the
 * tenant layout beside the analytics-sync / export managers). Renders a floating
 * progress widget whenever an import is running; nothing otherwise. Keyed by
 * workflowId so a new import starts expanded.
 */
export function CatalogImportProgressManager() {
  const active = useActiveCatalogImport();
  if (!active) return null;
  return <CatalogImportProgressToast key={active.workflowId} active={active} />;
}

/**
 * The floating widget: an expandable card (source, %, overall bar, imported/total
 * + ETA) that navigates to the Complete Import screen on click, or a minimized
 * tiny pie showing just the percentage. Polls the workflow for live numbers and
 * hides itself once the import reaches a terminal state. Progress and ETA come from
 * the same helpers as the Complete Import card (catalog-level across a resumed
 * chain; no ETA while paused or retrying a page), so the two never disagree.
 */
function CatalogImportProgressToast({ active }: { active: ActiveCatalogImportWithSource }) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);
  const { data } = useWorkflowImportStatus(active.workflowId, active.source);
  // Same reads as the card (shared query cache): the run chain for catalog-level
  // progress, the current page to tell a retry from a running fetch.
  const runId = data?.status === 'in_progress' ? (data.pipelineRunId ?? null) : null;
  const lineage = useIngestRunLineage(runId);
  const ingestRun = lineage.isError && isNotIngestionRun(lineage.error) ? null : runId;
  const { data: currentPage } = useIngestCurrentPage(ingestRun, data?.phase === 'running');

  // On completion, refresh the onboarding aggregate so the rest of the app (the
  // experiments gate, sidebar, etc.) sees COMPLETE; the widget then unmounts as
  // `useActiveCatalogImport` stops matching an IN_PROGRESS channel.
  useEffect(() => {
    if (data && data.status !== 'in_progress') {
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.status() });
    }
  }, [data, queryClient]);

  // Only render while actively importing — wait for the first reading and drop
  // out on terminal (completed / failed); the full screen owns those states.
  if (data?.status !== 'in_progress') return null;

  const progress = computeCatalogProgress(data, findRun(lineage.data, runId), lineage.data);
  const percent = progress.percent;
  const etaSeconds = visibleEtaSeconds(data, currentPage);

  const openScreen = () => {
    // Branch so each navigate() gets a literal `to` the router types precisely.
    if (active.to === '/onboarding/integration/meta/complete-import') {
      void navigate({ to: active.to, search: { workflowId: active.workflowId } });
    } else {
      void navigate({ to: active.to, search: { workflowId: active.workflowId } });
    }
  };
  const onCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openScreen();
    }
  };

  return (
    <div className="fixed right-4 bottom-4 z-50">
      {collapsed ? (
        <button
          type="button"
          aria-label={t('catalogImportToast.expand')}
          onClick={() => {
            setCollapsed(false);
          }}
          className="bg-background flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border shadow-lg transition-transform hover:scale-105"
        >
          <MiniPie percent={percent} />
        </button>
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-label={t('catalogImportToast.open')}
          onClick={openScreen}
          onKeyDown={onCardKeyDown}
          className="bg-background w-80 max-w-[calc(100vw-2rem)] cursor-pointer rounded-xl border p-4 shadow-lg transition-shadow hover:shadow-xl"
        >
          {/* Header — title + percentage + minimize */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-foreground text-sm font-semibold">
              {t('catalogImportToast.title')}
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-primary text-xs font-semibold">{percent}%</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('catalogImportToast.collapse')}
                className="text-muted-foreground hover:text-foreground h-6 w-6 shrink-0 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setCollapsed(true);
                }}
              >
                <Minimize2 aria-hidden="true" className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Source + overall progress bar */}
          <p className="text-muted-foreground mt-0.5 truncate text-xs">{active.source}</p>
          <div className="mt-2">
            <MiniBar percent={percent} />
          </div>

          {/* Imported of total · time left */}
          <p className="text-muted-foreground mt-2 text-xs">
            {t('catalogImportToast.importedOfTotal', {
              imported: progress.imported,
              total: progress.total,
            })}
            {etaSeconds != null && (
              <>
                {' · '}
                {t('catalogImportToast.timeLeft', {
                  time: formatEtaShort(etaSeconds),
                })}
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

/** Thin horizontal progress bar — mirrors the analytics-sync toast's `MiniBar`. */
function MiniBar({ percent }: { percent: number }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-1.5 w-full overflow-hidden rounded-full"
      style={{ backgroundColor: 'var(--surface-progress-track)' }}
    >
      <div
        className="bg-primary h-full rounded-full transition-[width] duration-300"
        style={{ width: `${String(clamped)}%` }}
      />
    </div>
  );
}

const PIE_SIZE = 44;
const PIE_STROKE = 4;
const PIE_RADIUS = (PIE_SIZE - PIE_STROKE) / 2;
const PIE_CIRCUMFERENCE = 2 * Math.PI * PIE_RADIUS;

/** Tiny circular progress with the percentage in the centre — the minimized view. */
function MiniPie({ percent }: { percent: number }) {
  const clamped = Math.max(0, Math.min(100, percent));
  const dashOffset = PIE_CIRCUMFERENCE * (1 - clamped / 100);
  return (
    <span
      className="relative flex items-center justify-center"
      style={{ width: PIE_SIZE, height: PIE_SIZE }}
    >
      <svg width={PIE_SIZE} height={PIE_SIZE} className="-rotate-90">
        <circle
          cx={PIE_SIZE / 2}
          cy={PIE_SIZE / 2}
          r={PIE_RADIUS}
          fill="none"
          stroke="var(--border)"
          strokeWidth={PIE_STROKE}
          strokeOpacity={0.4}
        />
        <circle
          cx={PIE_SIZE / 2}
          cy={PIE_SIZE / 2}
          r={PIE_RADIUS}
          fill="none"
          stroke="var(--primary)"
          strokeWidth={PIE_STROKE}
          strokeLinecap="round"
          strokeDasharray={PIE_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          className="transition-[stroke-dashoffset] duration-500 ease-out"
        />
      </svg>
      <span className="text-primary absolute inset-0 flex items-center justify-center text-[11px] font-semibold">
        {Math.round(clamped)}%
      </span>
    </span>
  );
}

/** Seconds → compact "1h 12m" / "7m" / "45s" for the widget's time-left line. */
function formatEtaShort(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return minutes > 0 ? `${String(hours)}h ${String(minutes)}m` : `${String(hours)}h`;
  if (minutes > 0) return `${String(minutes)}m`;
  return `${String(total)}s`;
}
